import { useCallback, useState } from 'react'

import { ApiException } from '../api/client'
import { unlock } from './api'
import { clearSession, readSession, writeSession } from './session-storage'
import type { PortalSession } from './types'

/**
 * El `visitor_id` sí se persiste; el token de sesión NO.
 *
 * Motivo: son cosas distintas. El visitor_id no es una credencial —no da
 * acceso a nada por sí solo— y sirve para que el cliente recupere SUS
 * favoritos al volver mañana. El token de sesión sí es una credencial y vive
 * sólo en memoria, así que un XSS no se lleva una sesión persistente.
 *
 * Se guarda por enlace: la misma persona puede tener varias galerías del mismo
 * estudio y sus selecciones no deben mezclarse.
 */
function visitorKey(linkToken: string): string {
  return `chasquitambo:visitor:${linkToken.slice(0, 16)}`
}

function readVisitorId(linkToken: string): string | null {
  try {
    return localStorage.getItem(visitorKey(linkToken))
  } catch {
    // Safari en navegación privada lanza al tocar localStorage. Perder el
    // visitor_id degrada la experiencia (favoritos nuevos cada visita) pero no
    // debe impedir ver la galería.
    return null
  }
}

function storeVisitorId(linkToken: string, visitorId: string): void {
  try {
    localStorage.setItem(visitorKey(linkToken), visitorId)
  } catch {
    /* sin persistencia; la sesión actual sigue funcionando */
  }
}

type Status =
  | 'idle'
  | 'unlocking'
  | 'ready'
  | 'password_required'
  | 'unavailable'
  /* La ENTREGA caducó, que no es lo mismo que un enlace inservible: ahí el
     estudio puso fecha a propósito, y el cliente tiene a quién preguntar. */
  | 'expired'

export function usePortalSession(linkToken: string) {
  // Se restaura ANTES del primer render: si esperásemos a un efecto, el
  // cliente vería la pantalla de contraseña parpadear en cada navegación.
  const restored = readSession(linkToken)

  const [session, setSession] = useState<PortalSession | null>(restored)
  const [status, setStatus] = useState<Status>(restored !== null ? 'ready' : 'idle')
  const [error, setError] = useState<string | null>(null)

  const open = useCallback(
    async (password: string | null) => {
      setStatus('unlocking')
      setError(null)

      try {
        const result = await unlock(linkToken, password, readVisitorId(linkToken))

        storeVisitorId(linkToken, result.visitor_id)
        writeSession(linkToken, result)
        setSession(result)
        setStatus('ready')
      } catch (caught) {
        if (caught instanceof ApiException) {
          if (caught.error.code === 'share_password_required') {
            // Primera visita a un enlace protegido: no es un error, es el
            // flujo normal. Se pide la contraseña sin mensaje de alarma.
            setStatus('password_required')

            return
          }

          if (caught.error.code === 'share_password_invalid') {
            setStatus('password_required')
            setError('La contraseña no es correcta.')

            return
          }

          if (caught.error.code === 'share_project_expired') {
            clearSession(linkToken)
            setStatus('expired')

            return
          }

          if (caught.error.code === 'share_link_unavailable') {
            clearSession(linkToken)
            setStatus('unavailable')

            return
          }
        }

        // Fallback genuinamente desconocido. Antes decía «revisa tu
        // conexión» y era engañoso: el caso que caía aquí era un 401 con el
        // código mal derivado, no un problema de red del cliente.
        setStatus('password_required')
        setError('No se pudo abrir la galería. Inténtalo de nuevo.')
      }
    },
    [linkToken],
  )

  /*
   | Marcar la entrega caducada desde dentro.
   |
   | La sesión del portal dura dos horas y la galería es la que se entera:
   | puede caducar con la pestaña abierta. Sin esto, el cliente se quedaba en
   | «no se pudo abrir la galería», que dice que algo falló cuando no falló
   | nada — su fotógrafo puso una fecha y llegó.
   */
  const markExpired = useCallback(() => {
    clearSession(linkToken)
    setSession(null)
    setStatus('expired')
  }, [linkToken])

  return { session, status, error, open, markExpired }
}
